import type { AvailableFilters } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"

import { useCallback, useEffect, useState } from "react"

import { PUBLICATION_DATE_FILTER_OPTIONS } from "@/modules/jobAds/client/constants"
import {
  generateOptionsFromSearchConfigAndFacets,
  getInitialOptions,
} from "@/modules/jobAds/client/utils/generateOptions"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"

import { useJobAdsSearchParams } from "./useJobAdsSearchParams"

const availableSecondaryFiltersParams = {
  candidacyTypes: "candidacy_type",
  companyBusinessTypes: "company_business_type",
  companySectors: "company_sectors",
  durations: "contract_duration",
  jobCategories: "position_category_uuid",
  jobWithImpact: "job_with_impact",
  languages: "locale",
  publicationDate: "publication_date",
  remoteTypes: "remote_types",
  startingDates: "start_date",
  studyLevels: "study_levels",
  workExperiences: "work_experience_code",
} as const

export type SelectedSecondaryFiltersParam = keyof typeof availableSecondaryFiltersParams
type AppliedSecondaryFiltersParam =
  (typeof availableSecondaryFiltersParams)[keyof typeof availableSecondaryFiltersParams]

export type SelectedSecondaryFilters = Record<SelectedSecondaryFiltersParam, SimpleSelectOptions[]>

const emptyFilters: SelectedSecondaryFilters = {
  candidacyTypes: [],
  companyBusinessTypes: [],
  companySectors: [],
  durations: [],
  jobCategories: [],
  jobWithImpact: [],
  languages: [],
  publicationDate: [],
  remoteTypes: [],
  startingDates: [],
  studyLevels: [],
  workExperiences: [],
}

type UseSecondaryFilters = {
  secondaryFilters: SelectedSecondaryFilters
  selectedSecondaryFiltersNumber: number
  getSecondaryParameters: (newSecondaryFilters: SelectedSecondaryFilters) => AvailableFilters
}

export const useSecondaryFilters = (): UseSecondaryFilters => {
  const { careerCenterConf, currentUserSearchConfig, facets, isPublicationDateFilterEnabled } = usePageContext()
  const searchParams = useJobAdsSearchParams()

  const search = searchParams.toString()

  const [secondaryFilters, setSecondaryFilters] = useState(emptyFilters)
  const [selectedSecondaryFiltersNumber, setSelectedSecondaryFiltersNumber] = useState(0)

  const retrieveAppliedSecondaryFilter = useCallback(
    (param: AppliedSecondaryFiltersParam): SimpleSelectOptions[] => {
      if (isPublicationDateFilterEnabled && param === "publication_date") {
        return getInitialOptions({
          options: PUBLICATION_DATE_FILTER_OPTIONS.filter(option => option !== "no-time-limit").map(option => ({
            label: option,
            value: option,
          })),
          param: searchParams.getAll("publication_date"),
        })
      }

      return getInitialOptions({
        options: generateOptionsFromSearchConfigAndFacets({
          careerCenterConf,
          facets,
          filterNameFromFacet: param,
          filterNameFromSearchConfig: param,
          searchConfig: currentUserSearchConfig?.searchFilters || [],
        }),
        param: searchParams.getAll(param),
      })
    },
    // searchParams changes on every render so we use its stringified output
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [careerCenterConf, currentUserSearchConfig, facets, search]
  )

  useEffect(() => {
    const appliedSelectedSecondaryFilters: SelectedSecondaryFilters = { ...emptyFilters }

    Object.entries(availableSecondaryFiltersParams).forEach(filtersParam => {
      const selectedParam = filtersParam[0] as SelectedSecondaryFiltersParam
      const appliedParam = filtersParam[1]

      const appliedSecondaryFilter = retrieveAppliedSecondaryFilter(appliedParam)

      appliedSelectedSecondaryFilters[selectedParam] = appliedSecondaryFilter
    })

    setSecondaryFilters(appliedSelectedSecondaryFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    setSelectedSecondaryFiltersNumber(Object.values(secondaryFilters).flat().length)
  }, [secondaryFilters])

  const getSecondaryParameters = (newSecondaryFilters: SelectedSecondaryFilters): AvailableFilters => {
    const secondaryFiltersArray = Object.entries(newSecondaryFilters)
    const queryParamFilters: AvailableFilters = {}

    secondaryFiltersArray.forEach(([key, appliedFilter]) => {
      const queryKey = availableSecondaryFiltersParams[key as SelectedSecondaryFiltersParam]

      queryParamFilters[queryKey] = appliedFilter.map(filter => filter.value)
    })

    return queryParamFilters
  }

  return {
    getSecondaryParameters,
    secondaryFilters,
    selectedSecondaryFiltersNumber,
  }
}
